import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { parseVitalProgramConfigurationValue } from "@tellescope/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const useVitalProgramsAndThresholds = () => {
  const { t } = useTranslation("vitals");

  const { data: vitalPrograms } = useTellescopeInfiniteQuery({
    model: "configurations",
    method: "getSome",
    request: {
      limit: 500,
      filter: {
        type: "Vital Programs",
      },
    },
    select: (data) => {
      return data.pages.flat().reduce<{
        programs: ReturnType<typeof parseVitalProgramConfigurationValue>["programs"];
        configurationIds: Set<string>;
      }>(
        (acc, program) => {
          const { programs } = parseVitalProgramConfigurationValue(program.value);
          acc.programs.push(...programs);
          programs.forEach((program) => program.configurationIds.forEach((id) => acc.configurationIds.add(id)));
          return acc;
        },
        { programs: [], configurationIds: new Set<string>() }
      );
    },
  });

  const { data: thresholdsFromPrograms = [] } = useTellescopeInfiniteQuery({
    model: "vital_configurations",
    method: "getSome",
    request: {
      filter: {
        enduserId: {
          _exists: false,
        },
      },
    },
    enabled: !!vitalPrograms?.configurationIds,
    select: (data) => data.pages.flat(),
  });

  const programOptions = useMemo(
    () =>
      vitalPrograms?.programs.map((program) => ({
        value: program.title,
        label: program.title || t("Unknown Program"),
      })) ?? [],
    [vitalPrograms, t]
  );

  const thresholdOptions = useMemo(
    () =>
      thresholdsFromPrograms.map((threshold) => ({
        value: threshold.id,
        label: threshold.title,
      })),
    [thresholdsFromPrograms]
  );

  const defaultProgramConfigIds = vitalPrograms?.programs[0]?.configurationIds;

  return {
    vitalPrograms,
    thresholdsFromPrograms,
    programOptions,
    thresholdOptions,
    defaultProgramConfigIds,
  };
};
